/**
 * Resolución determinista de recursos visuales para nodos del Lienzo (Canvas).
 * 
 * Prioridades:
 * 1. Recurso explícito asignado al nodo (node.data.assetId o node.assetId)
 *    Permite selección manual de la Galería o asignación directa tras generación.
 * 2. Recurso generado directamente por este nodo (node.id === asset.nodeId)
 *    Respetando la coincidencia de tipo (imagen o vídeo).
 * 3. Recurso de la escena vinculada (respetando selectedAssetId):
 *    - Si node.type === "image": usa selectedAssetId si es imagen; si la escena tiene
 *      seleccionado un vídeo (ej. tras generar vídeo), busca la versión de imagen activa
 *      en scene.assetIds o data.assets para que el nodo de imagen no quede en negro.
 *    - Si node.type === "video": usa selectedAssetId si es vídeo; si no, busca vídeos
 *      en scene.assetIds o data.assets (projectVideos de la escena).
 *    - Fallback: muestra el activo de la escena disponible (ej. imagen de entrada de la toma).
 * 4. Para nodo de vídeo sin vídeo generado: si tiene un Start Frame conectado, muestra el
 *    fotograma inicial del nodo origen como previsualización.
 */
export const resolveNodeAsset = (node, data, assetById) => {
  if (!node) return null;

  const assetsMap = assetById || {};
  const isExplicitlyUnlinked = node.data?.assetSource === "unlinked";

  // 1. Prioridad 1: Recurso explícito asignado al nodo (manual o por generación)
  const direct = (node.assetId ? assetsMap[node.assetId] : null) ||
                 (node.data?.assetId ? assetsMap[node.data.assetId] : null);
  if (direct) return direct;

  // 2. Prioridad 2: Recurso generado por este nodo (node.id === asset.nodeId)
  // Omitido si el usuario desvinculó expresamente el vídeo de este nodo
  if (!isExplicitlyUnlinked) {
    const nodeAssets = Object.values(assetsMap).filter(
      (a) => a && a.nodeId === node.id && (node.type ? a.type === node.type : true)
    );
    if (nodeAssets.length > 0) {
      return nodeAssets.sort((a, b) => {
        const aTime = new Date(a.createdAt || 0).getTime();
        const bTime = new Date(b.createdAt || 0).getTime();
        if (aTime !== bTime) return bTime - aTime;
        return (a.id || "").localeCompare(b.id || "");
      })[0];
    }
  }

  // 3. Prioridad 3: Recurso de la escena vinculada
  const scene = (data?.scenes || []).find((s) => s.id === node.data?.sceneId);
  if (scene) {
    const selected = scene.selectedAssetId ? assetsMap[scene.selectedAssetId] : null;

    if (node.type === "image") {
      // Si la versión seleccionada de la escena es una imagen, usarla directamente
      if (selected && selected.type === "image") return selected;

      // Si la versión seleccionada no es imagen (ej. si la escena tiene seleccionado un vídeo),
      // buscar la imagen más reciente o activa de la escena
      const sceneImages = (scene.assetIds || [])
        .map((id) => assetsMap[id])
        .filter((a) => a && a.type === "image");
      if (sceneImages.length > 0) return sceneImages[sceneImages.length - 1];

      const projectImages = (data?.assets || [])
        .filter((a) => a.sceneId === scene.id && a.type === "image")
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      if (projectImages.length > 0) return projectImages[0];

      // Si la escena tiene un activo seleccionado (sin discriminar tipo), mostrarlo
      if (selected) return selected;
    } else if (node.type === "video" && !isExplicitlyUnlinked) {
      if (selected && selected.type === "video") return selected;

      const sceneVideos = (scene.assetIds || [])
        .map((id) => assetsMap[id])
        .filter((a) => a && a.type === "video");
      if (sceneVideos.length > 0) return sceneVideos[sceneVideos.length - 1];

      // Fallback: si hay vídeos generados para esta escena en los activos del proyecto
      const projectVideos = (data?.assets || [])
        .filter((a) => a.sceneId === scene.id && a.type === "video")
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      if (projectVideos.length > 0) return projectVideos[0];

      if (selected) return selected;
    }
  }

  // 4. Para nodo de vídeo sin vídeo generado: si tiene un Start Frame conectado, mostrar el fotograma inicial como preview
  if (node.type === "video") {
    const startEdge = (data?.edges || []).find(
      (e) => e.targetId === node.id && (e.role === "start_frame" || !e.role)
    );
    if (startEdge) {
      const sourceNode = (data?.nodes || []).find((n) => n.id === startEdge.sourceId);
      if (sourceNode && sourceNode.id !== node.id) {
        const inputAsset = resolveNodeAsset(sourceNode, data, assetsMap);
        if (inputAsset) return inputAsset;
      }
    }
  }

  return null;
};
